/**
 * Map saved-stats-filters API row (snake_case) to SavedFilter (camelCase).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FilterCondition, SavedFilter, SharedUser } from '@/features/smswiser-campaigns/types/saved-filter.types';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function asString(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function asFilterConditions(v: unknown): FilterCondition[] {
  if (!Array.isArray(v)) return [];
  return v.filter(
    (x): x is FilterCondition =>
      isRecord(x) &&
      typeof x.field === 'string' &&
      typeof x.operator === 'string' &&
      typeof x.value === 'string',
  );
}

function normalizeSharedWith(raw: unknown): SharedUser[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item): SharedUser => {
    if (typeof item === 'string') {
      return { userId: item, email: '', firstName: '', lastName: '' };
    }
    if (isRecord(item) && typeof item.userId === 'string') {
      return {
        userId: item.userId,
        email: asString(item.email),
        firstName: asString(item.firstName),
        lastName: asString(item.lastName),
      };
    }
    return { userId: String(item), email: '', firstName: '', lastName: '' };
  });
}

/** Accepts Nest/Prisma JSON row (snake_case and/or camelCase). */
export function mapSavedFilterFromApi(row: unknown): SavedFilter {
  if (!isRecord(row)) {
    throw new Error('Invalid saved filter payload');
  }
  const userId = asString(row.user_id) || asString(row.userId);
  const dateFromRaw = row.date_from ?? row.dateFrom;
  const dateToRaw = row.date_to ?? row.dateTo;
  const presetRaw = row.date_range_preset ?? row.dateRangePreset;
  const toDmyOrNull = (v: unknown): string | null =>
    v == null || v === '' ? null : asString(v);
  return {
    id: asString(row.id),
    userId,
    name: asString(row.name),
    color: asString(row.color),
    tabs: Array.isArray(row.tabs) ? row.tabs.filter((t): t is string => typeof t === 'string') : [],
    filters: asFilterConditions(row.filters),
    dateFrom: toDmyOrNull(dateFromRaw),
    dateTo: toDmyOrNull(dateToRaw),
    dateToUntilToday:
      row.date_to_until_today === true || row.dateToUntilToday === true,
    dateRangePreset:
      presetRaw == null || presetRaw === ''
        ? null
        : asString(presetRaw),
    isOwner: row.isOwner === true,
    sharedWith: normalizeSharedWith(row.sharedWith),
    createdAt: asString(row.created_at) || asString(row.createdAt),
    updatedAt: asString(row.updated_at) || asString(row.updatedAt),
  };
}
